import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CURRENCIES, EU_COUNTRIES } from '@/lib/locale';
import { useRegions } from '@/lib/useRegions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared EU fields: country, region, tax country, intra-EU VAT (VIES) and currency.
// `value` = { country, region, tax_country, vat_number, currency }; `onChange(partial)`.
const FiscalFields = ({ value, onChange, showCurrency = true, idPrefix = 'fiscal' }) => {
  const { t } = useTranslation();
  const [vies, setVies] = useState(null);
  const [checking, setChecking] = useState(false);
  const regions = useRegions(value.country || 'ES');

  const validate = async () => {
    if (!value.vat_number) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/vies/validate`, { country_code: value.tax_country || value.country || 'ES', vat_number: value.vat_number });
      setVies(res.data);
    } catch { setVies({ valid: null }); } finally { setChecking(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-fields`}>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-sm">{t('auth.country')}</Label>
          <Select value={value.country || 'ES'} onValueChange={(v) => onChange({ country: v, region: '' })}>
            <SelectTrigger data-testid={`${idPrefix}-country`} className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {EU_COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-sm">{t('auth.region')}</Label>
          <Input
            data-testid={`${idPrefix}-region`}
            list={`${idPrefix}-regions`}
            value={value.region || ''}
            onChange={(e) => onChange({ region: e.target.value })}
            placeholder={regions[0] ? `Ej: ${regions[0]}` : '—'}
          />
          <datalist id={`${idPrefix}-regions`}>{regions.map((p) => <option key={p} value={p} />)}</datalist>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-sm">{t('auth.taxCountry')}</Label>
          <Select value={value.tax_country || value.country || 'ES'} onValueChange={(v) => onChange({ tax_country: v })}>
            <SelectTrigger data-testid={`${idPrefix}-tax-country`} className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {EU_COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showCurrency && (
          <div className="space-y-1">
            <Label className="text-sm">{t('auth.currency')}</Label>
            <Select value={value.currency || 'EUR'} onValueChange={(v) => onChange({ currency: v })}>
              <SelectTrigger data-testid={`${idPrefix}-currency`} className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label className="text-sm">{t('auth.vat')}</Label>
        <div className="flex gap-2">
          <Input
            data-testid={`${idPrefix}-vat`}
            value={value.vat_number || ''}
            onChange={(e) => { onChange({ vat_number: e.target.value.toUpperCase() }); setVies(null); }}
            placeholder="ESB12345678"
          />
          <Button type="button" variant="outline" onClick={validate} disabled={checking || !value.vat_number} data-testid={`${idPrefix}-vies-button`}>
            {checking ? '…' : t('auth.validateVies')}
          </Button>
        </div>
        <p className="text-xs text-slate-500">{t('auth.vatHint')}</p>
        {vies && (
          <p className={`text-xs flex items-center gap-1 ${vies.valid ? 'text-emerald-700' : vies.valid === false ? 'text-red-600' : 'text-amber-700'}`} data-testid={`${idPrefix}-vies-result`}>
            {vies.valid ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {vies.valid ? `${t('auth.viesValid')}${vies.name && vies.name !== '---' ? ` · ${vies.name}` : ''}` : vies.valid === false ? t('auth.viesInvalid') : t('auth.viesUnavailable')}
          </p>
        )}
      </div>
    </div>
  );
};

export default FiscalFields;
